import { useState } from 'react';
import { Alert, Button, Select, Spin, Tag } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { ExpandDown } from '../../../assets/CustomIcons/ExpandDown';
import {
  useGetGithubMappableWorkspaces,
  useGetGithubRepositories,
  useSetRepositoryMappingMutation,
} from '../../../serverState/github';
import { GithubRepositoryMapping } from '../../../types/Github';

/**
 * Maps each visible GitHub repository to at most one workspace. Clearing the selection
 * unmaps it - the server treats `workspaceId: null` as the unmap instruction.
 *
 * `repoId` is a STRING on the wire (spec decision 12): GitHub ids exceed the safe integer
 * range, so both sides store and send them as strings. Never parse one to a number.
 *
 * DESIGN: a labelled two-column list rather than a bare stack of rows - without a header
 * the dropdowns read as unexplained controls. Colours follow the design-system scale
 * (`text-brand-grey-dark` for names, `text-neutral-900` for supporting copy), and bordered
 * elements carry an explicit `border-solid` because Tailwind preflight is disabled
 * repo-wide. Rows stay `<li>` elements: the tests locate a repository's row through them.
 *
 * COLLAPSED BY DEFAULT, and `defaultExpanded` exists because the two callers want opposite
 * things. In Settings the table is reference material behind a summary you can read at a
 * glance ("1/3 Mapped"). On the install screen it is the task, and the Continue button
 * there waits on a mapping - collapsing it would put that gate behind a puzzle.
 *
 * The toggle is a `role="button"` div rather than a `<button>`: the repo bans raw buttons,
 * and a heading is flow content that cannot legally live inside one anyway. Enter and
 * Space are wired by hand so that costs no keyboard access.
 */
export default function RepositoryMappingTable({
  organizationId,
  defaultExpanded = false,
}: {
  organizationId: number;
  defaultExpanded?: boolean;
}) {
  const repositories = useGetGithubRepositories(organizationId);
  const workspaces = useGetGithubMappableWorkspaces(organizationId);
  const setMappingMutation = useSetRepositoryMappingMutation();

  const [error, setError] = useState<string | null>(null);
  const [savingRepoId, setSavingRepoId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(defaultExpanded);

  const onChange = async (repoId: string, workspaceId: number | null) => {
    setError(null);
    setSavingRepoId(repoId);
    try {
      await setMappingMutation.mutateAsync({ organizationId, repoId, workspaceId });
    } catch {
      setError('Could not save that mapping. Please try again.');
    } finally {
      setSavingRepoId(null);
    }
  };

  const rows = repositories.data ?? [];
  const mappedCount = rows.filter((repository) => repository.workspaceId !== null).length;

  // Imperative on purpose: this table is the one thing standing between a connected
  // installation and an integration that actually does something, and it renders both in
  // Settings and as the second step of the install flow, where "Repositories" read as a
  // section label rather than as the thing left to do.
  const toggle = (
    <div
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      onClick={() => setExpanded((open) => !open)}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        setExpanded((open) => !open);
      }}
      className="flex cursor-pointer items-center gap-1 rounded"
    >
      <h3 className="m-0 text-base font-semibold leading-normal text-brand-grey-dark">Map your Repositories</h3>
      <ExpandDown className={`shrink-0 text-neutral-900 ${expanded ? 'rotate-180' : ''}`} />
    </div>
  );

  // The summary the collapsed state exists for: how much of this installation is actually
  // wired up, without opening anything. Withheld until the query settles rather than
  // rendering "0/0 Mapped", which reads as a connected installation with nothing in it.
  //
  // `bg-neutral-200` stands in for the design's `effects/hover-light` (rgba(0,0,0,0.06)),
  // which this project has no token for - and a raw black alpha would not invert in dark
  // mode, where the card behind it is already dark.
  const summary =
    repositories.isLoading || repositories.isError ? null : (
      <span className="min-w-5 rounded bg-neutral-200 p-0.5 text-center text-xs font-semibold leading-4 text-neutral-900">
        {mappedCount}/{rows.length} Mapped
      </span>
    );
  const workspacesLoading = workspaces.isLoading;
  const workspaceOptions = (workspaces.data ?? []).map((workspace) => ({
    value: workspace.id,
    label: workspace.name,
  }));

  // The options are the workspaces this caller OWNS, which is exactly what the
  // server will accept. Mapping a repository into a workspace requires ownership
  // of it, because a mapping carries that workspace's task titles out to GitHub
  // and pull request comments back in, so organization admin alone must not be
  // able to arrange it for a workspace it cannot otherwise read. Offering a
  // wider set here would only produce a 403 on save.
  //
  // The fallback below is now a NORMAL case rather than two queries disagreeing:
  // a repository mapped to a workspace this caller does not own is legitimately
  // absent from the list, and its name still has to render. `workspaceName`
  // rides along on the repository row, so the bare-integer branch stays
  // unreachable in practice.
  const optionsFor = (repository: GithubRepositoryMapping) => {
    const { workspaceId, workspaceName } = repository;
    if (workspaceId === null || workspaceOptions.some((option) => option.value === workspaceId)) {
      return workspaceOptions;
    }
    return [...workspaceOptions, { value: workspaceId, label: workspaceName ?? `Workspace #${workspaceId}` }];
  };

  return (
    <div className="mt-8">
      <div className="flex flex-wrap items-center gap-2">
        {toggle}
        {summary}
      </div>

      {!expanded ? null : repositories.isLoading ? (
        <div className="mt-4 flex justify-center">
          <Spin />
        </div>
      ) : repositories.isError ? (
        <>
          <p className="m-0 mt-2 text-sm text-neutral-900">
            Could not load the repositories visible to this installation. Please try again.
          </p>
          <Button className="mt-3" onClick={() => void repositories.refetch()}>
            Retry
          </Button>
        </>
      ) : (
        <>
          <p className="m-0 mt-1 text-sm leading-relaxed text-neutral-900">
            Map a repository to a workspace so its pull requests can link to that workspace&apos;s tasks. Clear the
            selection to stop syncing a repository. Only pull requests opened after you map a repository are linked -
            existing open pull requests are not scanned.
          </p>

          {error && <Alert className="mt-4" type="error" showIcon={true} message={error} />}

          {rows.length === 0 ? (
            <p className="m-0 mt-4 rounded-lg bg-brand-grey-light p-4 text-sm leading-relaxed text-neutral-900">
              No repositories are visible to this installation yet. Grant the Taskonio app access to repositories on
              GitHub, then reload this page.
            </p>
          ) : (
            <>
              {/* An empty workspace list is now a legitimate settled state, not a
                  failure: the caller may simply own no workspace here. Left
                  unexplained it renders as a row of dead dropdowns that reads as
                  a bug, so it gets said out loud.

                  The error case is separated from the empty one deliberately.
                  Before ownership scoping the two were indistinguishable and it
                  did not matter; now, telling someone they own nothing when the
                  request merely failed would be a lie. */}
              {workspaces.isError ? (
                <div className="mt-4 rounded-lg bg-brand-grey-light p-4">
                  <p className="m-0 text-sm leading-relaxed text-neutral-900">
                    Could not load the workspaces you can map to. Any mapping already saved is still shown below.
                  </p>
                  <Button className="mt-3" onClick={() => void workspaces.refetch()}>
                    Retry
                  </Button>
                </div>
              ) : !workspacesLoading && workspaceOptions.length === 0 ? (
                <p className="m-0 mt-4 rounded-lg bg-brand-grey-light p-4 text-sm leading-relaxed text-neutral-900">
                  You do not own a workspace in this organization, so there is nothing to map a repository to. Only a
                  workspace&apos;s owner can connect a repository to it. You can still clear a mapping that is already
                  saved.
                </p>
              ) : null}

              <div className="mt-4 flex items-center justify-between gap-4 border-0 border-b border-solid border-neutral-300 pb-2">
                <span className="text-xs font-medium uppercase tracking-wide text-neutral-900">Repository</span>
                <span className="w-56 shrink-0 text-xs font-medium uppercase tracking-wide text-neutral-900">
                  Workspace
                </span>
              </div>
              <ul className="m-0 list-none p-0">
                {rows.map((repository) => (
                  <li
                    key={repository.repoId}
                    className="flex items-center justify-between gap-4 border-0 border-b border-solid border-neutral-300 py-3 last:border-b-0"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="truncate text-sm font-medium text-brand-grey-dark">{repository.fullName}</span>
                      {repository.private && (
                        <Tag className="m-0 shrink-0" icon={<LockOutlined />}>
                          Private
                        </Tag>
                      )}
                    </div>
                    <Select
                      className="w-56 shrink-0"
                      allowClear={true}
                      placeholder="Not mapped"
                      value={repository.workspaceId ?? undefined}
                      options={optionsFor(repository)}
                      loading={workspacesLoading || savingRepoId === repository.repoId}
                      // Only the row being saved locks. Disabling every row while one request
                      // is in flight made a single slow save freeze the whole table.
                      disabled={workspacesLoading || savingRepoId === repository.repoId}
                      onChange={(value) => void onChange(repository.repoId, value ?? null)}
                    />
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </div>
  );
}
